/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Users,
  Clock,
  Sparkles,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Search,
  UserCheck,
  Tag,
  ShieldCheck,
  Plus,
  GripVertical,
} from 'lucide-react';
import { ReservationPlan, TablePlan, Permissions } from './types.ts';
import { proposerAttributionAuto, evaluerStatutTable, chevauchementCreneau, heureDe } from './logiquePure.ts';

interface ColonneReservationsProps {
  reservations: ReservationPlan[];
  tables: TablePlan[];
  heureAffichee: string;
  permissions: Permissions;
  reservationSelectionneeId?: string;
  tableSelectionneeId?: string;
  onSelectionnerReservation: (reservationId: string) => void;
  onAttribuer: (reservationId: string, tableIds: string[]) => Promise<{ ok: boolean; message?: string }>;
}

export const ColonneReservations: React.FC<ColonneReservationsProps> = ({
  reservations,
  tables,
  heureAffichee,
  permissions,
  reservationSelectionneeId,
  tableSelectionneeId,
  onSelectionnerReservation,
  onAttribuer,
}) => {
  const [onglet, setOnglet] = useState<'a_placer' | 'toutes'>('a_placer');
  const [recherche, setRecherche] = useState<string>('');
  const [messageAttribution, setMessageAttribution] = useState<{ id: string; type: 'succes' | 'erreur'; texte: string } | null>(null);
  // Message global (la carte quitte la liste « À placer » dès qu'elle est placée : son message propre disparaîtrait avec elle).
  const [messageGlobal, setMessageGlobal] = useState<{ type: 'succes' | 'erreur'; texte: string } | null>(null);
  // Glisser-déposer d'une réservation vers une table du plan (événements pointeur, sans bibliothèque) : « fantôme » qui suit le pointeur, table visée surlignée.
  const [fantome, setFantome] = useState<{ x: number; y: number; texte: string; cible: string | null; ajouter: boolean } | null>(null);
  const glisse = useRef<{ res: ReservationPlan; x0: number; y0: number; actif: boolean; cible: string | null; ajouter: boolean } | null>(null);
  const ignorerClic = useRef(false);
  const dernier = useRef({ tables, reservations });
  dernier.current = { tables, reservations };

  // Filtrer les réservations sans table ("À placer") triées par heure
  const aPlacer = reservations
    .filter((r) => r.tableIds.length === 0 && r.statut !== 'annulee' && r.statut !== 'no_show')
    .sort((a, b) => a.debut.localeCompare(b.debut));

  const toutesFiltrees = reservations
    .filter((r) => {
      if (!recherche.trim()) return true;
      const q = recherche.toLowerCase();
      return (
        r.nom.toLowerCase().includes(q) ||
        r.statut.toLowerCase().includes(q) ||
        r.tableIds.some((tid) => tables.find((t) => t.id === tid)?.nom.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => a.debut.localeCompare(b.debut));

  const listeAffichee = onglet === 'a_placer' ? aPlacer : toutesFiltrees;

  const accepterAttributionAuto = async (reservation: ReservationPlan, tableIds: string[]) => {
    const res = await onAttribuer(reservation.id, tableIds);
    if (res.ok) {
      setMessageAttribution({
        id: reservation.id,
        type: 'succes',
        texte: `Attribué à ${tableIds.map((id) => tables.find((t) => t.id === id)?.nom || id).join(', ')}`,
      });
      setTimeout(() => setMessageAttribution(null), 3000);
    } else {
      setMessageAttribution({
        id: reservation.id,
        type: 'erreur',
        texte: res.message || 'Impossible d’attribuer.',
      });
    }
  };

  /** Place la réservation sur une table (ou l'ajoute à ses tables avec « ajouter ») : même vérification de créneau que le bouton « Assigner », puis l'attribution du serveur. */
  const placerSur = async (reservation: ReservationPlan, tableCible: TablePlan, ajouter = false) => {
    const annoncer = (type: 'succes' | 'erreur', texte: string) => { setMessageAttribution({ id: reservation.id, type, texte }); setMessageGlobal({ type, texte: `${reservation.nom} : ${texte}` }); };
    if (tableCible.bloquee) { annoncer('erreur', `Table ${tableCible.nom} bloquée${tableCible.motifBlocage ? ` (${tableCible.motifBlocage})` : ''}`); return; }
    const conflit = reservations.find((r) => r.id !== reservation.id && r.tableIds.includes(tableCible.id) && r.statut !== 'annulee' && r.statut !== 'no_show' && chevauchementCreneau(r.debut, r.fin, reservation.debut, reservation.fin));
    if (conflit) { annoncer('erreur', `Table ${tableCible.nom} déjà prise à ${heureDe(conflit.debut)} par ${conflit.nom}`); return; }
    const ids = ajouter ? [...new Set([...reservation.tableIds, tableCible.id])] : [tableCible.id];
    const res = await onAttribuer(reservation.id, ids);
    if (res.ok) {
      annoncer('succes', `placée sur ${ids.map((id) => tables.find((t) => t.id === id)?.nom || id).join(' + ')}`);
      setTimeout(() => { setMessageAttribution(null); setMessageGlobal(null); }, 3000);
    } else annoncer('erreur', res.message || 'Erreur d’attribution');
  };

  const surDebutGlisser = (e: React.PointerEvent, res: ReservationPlan, depuisPoignee: boolean) => {
    if (!permissions.attribuer || e.button !== 0) return;
    if (!depuisPoignee && e.pointerType !== 'mouse') return; // au doigt, seul le repère de saisie fait glisser : le reste de la carte fait défiler la liste
    if ((e.target as HTMLElement).closest('button, input, a')) return;
    glisse.current = { res, x0: e.clientX, y0: e.clientY, actif: false, cible: null, ajouter: false };
  };

  useEffect(() => {
    const cibleSous = (x: number, y: number): string | null => (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('[data-table-id]')?.getAttribute('data-table-id') || null;
    const surligner = (id: string | null) => {
      document.querySelectorAll('[data-cible-depot]').forEach((n) => n.removeAttribute('data-cible-depot'));
      if (id) document.querySelector(`[data-table-id="${CSS.escape(id)}"]`)?.setAttribute('data-cible-depot', 'true');
    };
    const fin = () => { glisse.current = null; surligner(null); setFantome(null); };
    const surMouvement = (e: PointerEvent) => {
      const g = glisse.current; if (!g) return;
      if (!g.actif) { if (Math.hypot(e.clientX - g.x0, e.clientY - g.y0) < 6) return; g.actif = true; }
      e.preventDefault();
      g.cible = cibleSous(e.clientX, e.clientY); g.ajouter = e.shiftKey; surligner(g.cible);
      setFantome({ x: e.clientX, y: e.clientY, texte: `${g.res.nom} · ${g.res.couverts} pers.`, cible: g.cible, ajouter: e.shiftKey });
    };
    const surRelachement = (e: PointerEvent) => {
      const g = glisse.current; if (!g) return;
      if (g.actif) {
        ignorerClic.current = true; setTimeout(() => { ignorerClic.current = false; }, 0); // le clic qui suit le relâchement ne doit pas sélectionner la carte
        const cible = cibleSous(e.clientX, e.clientY); const table = cible ? dernier.current.tables.find((t) => t.id === cible) : undefined;
        const res = dernier.current.reservations.find((r) => r.id === g.res.id) || g.res;
        fin();
        if (table) void placerRef.current(res, table, e.shiftKey);
      } else glisse.current = null;
    };
    const surTouche = (e: KeyboardEvent) => { if (e.key === 'Escape' && glisse.current?.actif) { e.stopPropagation(); fin(); } };
    window.addEventListener('pointermove', surMouvement, { passive: false });
    window.addEventListener('pointerup', surRelachement);
    window.addEventListener('pointercancel', fin);
    window.addEventListener('keydown', surTouche, true);
    return () => { window.removeEventListener('pointermove', surMouvement); window.removeEventListener('pointerup', surRelachement); window.removeEventListener('pointercancel', fin); window.removeEventListener('keydown', surTouche, true); surligner(null); };
  }, []);
  const placerRef = useRef(placerSur);
  placerRef.current = placerSur;

  const assignerATableSelectionnee = async (reservation: ReservationPlan) => {
    if (!tableSelectionneeId) return;
    const tableCible = tables.find((t) => t.id === tableSelectionneeId);
    if (!tableCible) return;

    // Vérifier si la table est déjà prise sur ce créneau
    const conflit = reservations.find(
      (r) =>
        r.id !== reservation.id &&
        r.tableIds.includes(tableCible.id) &&
        r.statut !== 'annulee' &&
        r.statut !== 'no_show' &&
        chevauchementCreneau(r.debut, r.fin, reservation.debut, reservation.fin)
    );

    if (conflit) {
      setMessageAttribution({
        id: reservation.id,
        type: 'erreur',
        texte: `Table ${tableCible.nom} déjà prise à ${heureDe(conflit.debut)} par ${conflit.nom}`,
      });
      return;
    }

    const res = await onAttribuer(reservation.id, [tableCible.id]);
    if (res.ok) {
      setMessageAttribution({
        id: reservation.id,
        type: 'succes',
        texte: `Placée sur table ${tableCible.nom}`,
      });
      setTimeout(() => setMessageAttribution(null), 3000);
    } else {
      setMessageAttribution({
        id: reservation.id,
        type: 'erreur',
        texte: res.message || 'Erreur d’attribution',
      });
    }
  };

  return (
    <div
      className="flex flex-col h-full bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 w-full sm:w-80 md:w-96 select-none"
      role="region"
      aria-label="Colonne des réservations"
    >
      {/* En-tête des onglets */}
      <div className="p-3 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg text-xs font-semibold">
          <button
            type="button"
            onClick={() => setOnglet('a_placer')}
            className={`flex-1 py-1.5 px-2 rounded-md flex items-center justify-center gap-1.5 cursor-pointer min-h-[36px] transition-colors ${
              onglet === 'a_placer'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-xs'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <span>À placer</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                aPlacer.length > 0
                  ? 'pds-compteur-alerte'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
              }`}
            >
              {aPlacer.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setOnglet('toutes')}
            className={`flex-1 py-1.5 px-2 rounded-md flex items-center justify-center gap-1.5 cursor-pointer min-h-[36px] transition-colors ${
              onglet === 'toutes'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-xs'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <span>Toutes ({reservations.length})</span>
          </button>
        </div>

        {/* Champ de recherche */}
        <div className="relative mt-2">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher nom, table..."
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-md text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
          />
        </div>
      </div>

      {messageGlobal && (
        <p role={messageGlobal.type === 'erreur' ? 'alert' : 'status'} className={`mx-3 mt-2 p-2 rounded-lg text-[11px] font-semibold ${messageGlobal.type === 'succes' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'}`}>{messageGlobal.texte}</p>
      )}

      {/* Liste déroulante des réservations */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2">
        {listeAffichee.length === 0 ? (
          <div className="py-12 text-center text-xs text-slate-400">
            {onglet === 'a_placer'
              ? 'Toutes les réservations sont placées sur le plan !'
              : 'Aucune réservation trouvée.'}
          </div>
        ) : (
          listeAffichee.map((res) => {
            const estSelectionnee = reservationSelectionneeId === res.id;
            const proposition = proposerAttributionAuto(res, tables, reservations);
            const aTable = res.tableIds.length > 0;
            const nomsTables = res.tableIds
              .map((id) => tables.find((t) => t.id === id)?.nom || id)
              .join(' + ');

            const msg = messageAttribution?.id === res.id ? messageAttribution : null;

            return (
              <div
                key={res.id}
                onClick={() => { if (!ignorerClic.current) onSelectionnerReservation(res.id); }}
                onPointerDown={(e) => surDebutGlisser(e, res, false)}
                className={`p-3 rounded-xl border text-xs cursor-pointer transition-all ${
                  estSelectionnee
                    ? 'border-sky-500 bg-sky-50/50 dark:bg-sky-950/30 shadow-xs ring-1 ring-sky-500'
                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700'
                }`}
              >
                {/* Ligne 1 : Nom et Couverts */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 font-bold text-sm text-slate-900 dark:text-slate-100">
                    {permissions.attribuer && (
                      <span
                        aria-hidden="true"
                        title="Glisser vers une table du plan (Maj : ajouter à ses tables)"
                        onPointerDown={(e) => { e.stopPropagation(); surDebutGlisser(e, res, true); }}
                        style={{ touchAction: 'none' }}
                        className="pds-poignee -ml-1 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-grab"
                      >
                        <GripVertical className="w-4 h-4" />
                      </span>
                    )}
                    <UserCheck className="w-3.5 h-3.5 text-slate-400" />
                    <span className="truncate max-w-[170px]">{res.nom}</span>
                  </div>

                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                    <Users className="w-3 h-3 text-slate-500" />
                    <span>{res.couverts} pers.</span>
                  </span>
                </div>

                {/* Ligne 2 : Heure et Statut */}
                <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mt-1.5">
                  <div className="flex items-center gap-1 font-mono text-[11px]">
                    <Clock className="w-3 h-3" />
                    <span>
                      {heureDe(res.debut)} - {heureDe(res.fin)}
                    </span>
                  </div>

                  {aTable ? (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-semibold text-[10px] border border-emerald-200 dark:border-emerald-800">
                      Table {nomsTables}
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-semibold text-[10px] border border-amber-200 dark:border-amber-800">
                      À placer
                    </span>
                  )}
                </div>

                {/* Allergènes : affichés STRICTEMENT si permissions.voirAllergenes */}
                {permissions.voirAllergenes && res.allergenes && res.allergenes.length > 0 && (
                  <div className="mt-2 p-1.5 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-[10px] flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 shrink-0 text-red-600" />
                    <span>Allergènes : {res.allergenes.join(', ')}</span>
                  </div>
                )}

                {/* Note ou étiquette */}
                {res.note && (
                  <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400 italic">
                    « {res.note} »
                  </p>
                )}

                {/* Feedback message attribution */}
                {msg && (
                  <div
                    className={`mt-2 p-1.5 rounded text-[11px] font-semibold flex items-center gap-1 ${
                      msg.type === 'succes'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                    }`}
                  >
                    <span>{msg.texte}</span>
                  </div>
                )}

                {/* Attribution automatique si non placée */}
                {!aTable && permissions.attribuer && (
                  <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                    {proposition.recommandee ? (
                      <div className="flex items-center justify-between gap-1.5 bg-slate-50 dark:bg-slate-800/60 p-1.5 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-1 text-[11px] text-slate-700 dark:text-slate-300 truncate">
                          <Sparkles className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                          <span className="truncate">{proposition.raison}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            accepterAttributionAuto(res, proposition.tableIds);
                          }}
                          aria-label={`Accepter l'attribution de la table ${proposition.nomTable}`}
                          className="px-2.5 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded text-[11px] font-bold cursor-pointer shrink-0 min-h-[36px] flex items-center gap-1"
                        >
                          <Check className="w-3 h-3 text-emerald-400" />
                          <span>Accepter</span>
                        </button>
                      </div>
                    ) : (
                      <div className="text-[10px] text-slate-400 italic">
                        {proposition.raison}
                      </div>
                    )}

                    {/* Raccourci pour placer sur la table actuellement sélectionnée sur le plan */}
                    {tableSelectionneeId && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          assignerATableSelectionnee(res);
                        }}
                        aria-label={`Assigner à la table sélectionnée`}
                        className="w-full py-1.5 px-2 rounded bg-sky-50 hover:bg-sky-100 dark:bg-sky-950/50 text-sky-800 dark:text-sky-300 border border-sky-200 dark:border-sky-800 font-semibold text-[11px] flex items-center justify-center gap-1 min-h-[36px] cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5 text-sky-600" />
                        <span>
                          Assigner à la table sélectionnée (
                          {tables.find((t) => t.id === tableSelectionneeId)?.nom})
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {fantome && (
        <div aria-hidden="true" style={{ position: 'fixed', left: fantome.x + 14, top: fantome.y + 14, pointerEvents: 'none', zIndex: 80 }}
          className={`px-2.5 py-1.5 rounded-lg shadow-lg text-[11px] font-bold border ${fantome.cible ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-slate-900 text-white border-slate-700'}`}>
          {fantome.texte}{fantome.cible ? (fantome.ajouter ? ' · ajouter à ses tables' : ' · déposer ici') : ''}
        </div>
      )}
    </div>
  );
};
